import { ReturnMethodTag } from '@/ui/components/return-method-tag'
import { useTranslation } from 'react-i18next'
import { RefundBadge } from '@/ui/components/process-details/refund-badge'
import { BackButton } from '@/ui/components/process-details'
import { Base, ChevronDownIcon } from '@itsrever/design-system'
import {
  PbBasicProcessDataPlatformEnum,
  PbLogisticsSummaryReturnMethodEnum
} from '@itsrever/rever-api'
import { useEffect, useRef, useState } from 'react'

const {
  MenuRoot,
  MenuTrigger,
  MenuPortal,
  MenuPositioner,
  MenuPopup,
  MenuItem
} = Base

const platformDisplayNames: Record<PbBasicProcessDataPlatformEnum, string> = {
  SHOPIFY: 'Shopify',
  WOOCOMMERCE: 'WooCommerce',
  SHOPWARE: 'Shopware',
  PRESTASHOP: 'PrestaShop',
  MAGENTO_V1: 'Magento',
  MAGENTO_V2: 'Magento',
  SALESFORCE: 'Salesforce',
  INDITEX: 'Inditex',
  COMMERCELAYER: 'Commerce Layer',
  CUSTOM_GENERIC: 'platform',
  CUSTOM_INTEGRATION_V2: 'platform',
  MOCK: 'platform'
}

interface ProcessInfoProps {
  purchasedDate?: Date
  returnDate: Date
  customerPrintedOrderId: string
  returnMethod?: PbLogisticsSummaryReturnMethodEnum
  amountUploadedFiles: number
  unauthorized: boolean
  isRefunded?: boolean
  isOnlyExchange?: boolean
  commitedAmount?: number
  orderPlatformUrl?: string
  platform?: PbBasicProcessDataPlatformEnum
  isCanceled?: boolean
}

const ProcessInfo: React.FC<ProcessInfoProps> = ({
  purchasedDate,
  returnDate,
  customerPrintedOrderId,
  returnMethod,
  amountUploadedFiles,
  unauthorized,
  isRefunded,
  isOnlyExchange,
  commitedAmount = 0,
  orderPlatformUrl,
  platform,
  isCanceled = false
}) => {
  const { i18n, t } = useTranslation()
  const [copied, setCopied] = useState(false)
  const copyResetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(
    () => () => {
      if (copyResetTimeoutRef.current) {
        clearTimeout(copyResetTimeoutRef.current)
      }
    },
    []
  )

  const displayReturnMethodTag =
    returnMethod &&
    returnMethod !== PbLogisticsSummaryReturnMethodEnum.NoReturnMethod

  const handleClickSeeUploadedDocs = () => {
    const uploadedDocs = document.getElementById('document-card')
    uploadedDocs?.scrollIntoView({ behavior: 'smooth' })
  }

  const handleCopyOrderNumber = async () => {
    try {
      await navigator.clipboard.writeText(customerPrintedOrderId)
      setCopied(true)
      if (copyResetTimeoutRef.current) {
        clearTimeout(copyResetTimeoutRef.current)
      }
      copyResetTimeoutRef.current = setTimeout(() => setCopied(false), 1500)
    } catch {
      return
    }
  }

  const handleMenuOpenChange = (open: boolean) => {
    if (!open) {
      if (copyResetTimeoutRef.current) {
        clearTimeout(copyResetTimeoutRef.current)
      }
      setCopied(false)
    }
  }

  const platformLabel = platform ? platformDisplayNames[platform] : 'platform'

  return (
    <div className="flex w-auto items-start gap-4">
      <BackButton />
      <div className="-mt-1">
        <div className="flex items-center gap-2">
          <MenuRoot onOpenChange={handleMenuOpenChange}>
            <MenuTrigger className="group -ml-2 flex items-center gap-1.5 rounded-md px-2 text-2xl font-medium text-slate-700 transition-colors hover:bg-neutral-200/60 data-[popup-open]:bg-neutral-200/60">
              {customerPrintedOrderId}
              <ChevronDownIcon className="size-2.5 text-slate-500" />
            </MenuTrigger>
            <MenuPortal>
              <MenuPositioner sideOffset={8} align="end">
                <MenuPopup>
                  <MenuItem
                    className="flex items-center gap-2 font-roobert font-medium"
                    onClick={handleCopyOrderNumber}
                    closeOnClick={false}
                  >
                    {copied ? (
                      <svg
                        width="19px"
                        height="19px"
                        strokeWidth="1.5"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        color="currentColor"
                        className="-ml-0.5"
                      >
                        <path
                          d="M5 13L9 17L19 7"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    ) : (
                      <svg
                        width="19px"
                        height="19px"
                        strokeWidth="1.5"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        color="currentColor"
                        className="-ml-0.5"
                      >
                        <path
                          d="M19.4 20H9.6C9.26863 20 9 19.7314 9 19.4V9.6C9 9.26863 9.26863 9 9.6 9H19.4C19.7314 9 20 9.26863 20 9.6V19.4C20 19.7314 19.7314 20 19.4 20Z"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M15 9V4.6C15 4.26863 14.7314 4 14.4 4H4.6C4.26863 4 4 4.26863 4 4.6V14.4C4 14.7314 4.26863 15 4.6 15H9"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                    <span className="grid">
                      <span aria-hidden className="invisible [grid-area:1/1]">
                        {t('process_details_page.copy_order_number')}
                      </span>
                      <span className="[grid-area:1/1]">
                        {copied
                          ? t('process_details_page.order_number_copied')
                          : t('process_details_page.copy_order_number')}
                      </span>
                    </span>
                  </MenuItem>
                  {orderPlatformUrl && (
                    <MenuItem
                      className="flex items-center gap-2.5 font-roobert font-medium"
                      render={
                        <a
                          href={orderPlatformUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        />
                      }
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="17px"
                        height="17px"
                        fill="currentColor"
                        viewBox="0 0 256 256"
                        className="-ml-0.5"
                      >
                        <path d="M224,104a8,8,0,0,1-16,0V59.32l-66.33,66.34a8,8,0,0,1-11.32-11.32L196.68,48H152a8,8,0,0,1,0-16h64a8,8,0,0,1,8,8Zm-40,24a8,8,0,0,0-8,8v72H48V80h72a8,8,0,0,0,0-16H48A16,16,0,0,0,32,80V208a16,16,0,0,0,16,16H176a16,16,0,0,0,16-16V136A8,8,0,0,0,184,128Z"></path>
                      </svg>
                      {t('process_details_page.view_in_platform', {
                        platform: platformLabel
                      })}
                    </MenuItem>
                  )}
                </MenuPopup>
              </MenuPositioner>
            </MenuPortal>
          </MenuRoot>
          {isCanceled ? (
            <span className="flex w-fit items-center gap-1.5 whitespace-nowrap rounded-full bg-[#ffecc0] px-3 py-1 text-xs font-medium text-[#624c18]">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <circle cx="12" cy="12" r="9" />
                <path d="M5.64 5.64l12.72 12.72" />
              </svg>
              {t('return_status.canceled')}
            </span>
          ) : null}
          {!unauthorized && (
            <>
              {displayReturnMethodTag ? (
                <ReturnMethodTag method={returnMethod} />
              ) : null}
              {!isOnlyExchange && commitedAmount > 0 ? (
                <RefundBadge isRefunded={isRefunded ?? false} />
              ) : null}
            </>
          )}
          {amountUploadedFiles ? (
            <p
              className="flex h-[1.42rem] min-w-[6.5rrem] cursor-pointer items-center justify-center gap-[0.3rem] whitespace-nowrap rounded-[5.7rem] bg-primary-light px-[0.7rem] py-[0.2rem] text-xs leading-5 text-white"
              onClick={handleClickSeeUploadedDocs}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                className="size-[17px] fill-white"
              >
                <path d="M4.5 6.75c0-.69.56-1.25 1.25-1.25h1.514c.473 0 .906.268 1.118.691l.17.342c.297.592.903.967 1.566.967h4.132c.69 0 1.25.56 1.25 1.25 0 .414.336.75.75.75s.75-.336.75-.75c0-1.519-1.231-2.75-2.75-2.75h-4.132c-.095 0-.181-.053-.224-.138l-.17-.342c-.466-.931-1.418-1.52-2.46-1.52h-1.514c-1.519 0-2.75 1.231-2.75 2.75v6.5c0 1.519 1.231 2.75 2.75 2.75h5c.414 0 .75-.336.75-.75s-.336-.75-.75-.75h-5c-.69 0-1.25-.56-1.25-1.25v-6.5Z" />
                <path d="M15.75 13.31v2.94c0 .414-.336.75-.75.75s-.75-.336-.75-.75v-2.94l-.72.72c-.293.293-.767.293-1.06 0-.293-.293-.293-.767 0-1.06l2-2c.293-.293.767-.293 1.06 0l2 2c.293.293.293.767 0 1.06-.293.293-.767.293-1.06 0l-.72-.72Z" />
              </svg>
              {amountUploadedFiles} doc
              {amountUploadedFiles > 1 ? 's' : ''}
            </p>
          ) : null}
        </div>
        <div className="flex items-center gap-1.5">
          <p className="flex items-center gap-1 text-xs font-light text-slate-500">
            {t('process_details_page.purchased_on')}:
            <span className="text-neutral-900">
              {purchasedDate
                ? purchasedDate.toLocaleString(i18n.language, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })
                : '-'}
            </span>
          </p>
          ·
          <p className="flex items-center gap-1 text-xs font-light text-slate-500">
            {t('process_details_page.returned_on')}:
            <span className="text-neutral-900">
              {returnDate.toLocaleString(i18n.language, {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </span>
          </p>
        </div>
      </div>
    </div>
  )
}

export default ProcessInfo
